/**
 * DeleteAccountSection
 *
 * Collapsed "Danger zone" disclosure at the bottom of the Account page that
 * reveals the account deletion flow. Triggers the exit survey and account
 * deletion via `useAuth`, and offers subscription cancellation as a less
 * destructive alternative for paying users.
 */
import React, { useState } from 'react';
import { ChevronRight, Trash2 } from 'lucide-react';
import { ExitSurveyModal } from './ExitSurveyModal';
import { useAuth } from '../../../context/auth';
import { redirectToBillingPortal } from '../../../services/stripeService';

export default function DeleteAccountSection({
  isDeleteModalOpen,
  setIsDeleteModalOpen,
  showCancelAlternative = false,
}) {
  const { deleteCurrentUserAccount, lastError, isTransitioning } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const confirmDeleteAccount = async (exitSurveyData = null) => {
    setIsDeleting(true);
    setDeleteError(null);

    try {
      await deleteCurrentUserAccount(exitSurveyData);
      // No need to close modal as we'll be redirected by AuthContext
    } catch (err) {
      console.error("Delete account failed:", err);
      setDeleteError(err.message || "Failed to delete account. Please try again.");
      setIsDeleting(false);
    }
  };

  const cancelSubscriptionInstead = async () => {
    await redirectToBillingPortal(`${window.location.origin}/app/account`);
  };

  return (
    <div className="mt-16 max-w-7xl border-t border-gray-200 pt-6 dark:border-gray-800">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
      >
        <ChevronRight className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
        Danger zone
      </button>

      {isExpanded && (
        <div className="mt-4 max-w-xl rounded-lg border border-red-200 p-4 dark:border-red-900/50">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Permanently delete your account and all associated data. This action cannot be undone.
          </p>
          <button
            type="button"
            onClick={() => setIsDeleteModalOpen(true)}
            disabled={isTransitioning || isDeleting}
            className="mt-3 inline-flex items-center gap-2 rounded-lg border border-red-300 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-900/50 dark:text-red-400 dark:hover:bg-red-900/20"
          >
            <Trash2 className="h-4 w-4" />
            {isTransitioning || isDeleting ? 'Deleting...' : 'Delete account…'}
          </button>
          {lastError && lastError.message.includes("delete") && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">Error: {lastError.message}</p>
          )}
        </div>
      )}

      <ExitSurveyModal
        isOpen={isDeleteModalOpen}
        onClose={() => !isDeleting && setIsDeleteModalOpen(false)}
        onDeleteAccount={confirmDeleteAccount}
        onCancelSubscription={showCancelAlternative ? cancelSubscriptionInstead : undefined}
        isLoading={isDeleting}
        error={deleteError}
      />
    </div>
  );
}
